import { FC, ReactNode } from "react";

import { Box } from "@mui/material";

import { LIVE_EDITOR_RAINBOW_GRADIENT } from "../liveEditorColors";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { Check } from "@/icons/filled";

interface LiveEditorColorButtonProps {
  color?: string;
  isSelected: boolean;
  onClick?: () => void;
  isDiagonal?: boolean;
  isGradient?: boolean;
  isOutlined?: boolean;
  icon?: ReactNode;
  badge?: ReactNode;
  hasSelectedCheck?: boolean;
  "aria-label"?: string;
  "data-test"?: string;
}

const BLACK_AND_WHITE_GRADIENT_BACKGROUND = "linear-gradient(135deg, white 50%, black 50%)";

export const LiveEditorColorButton: FC<LiveEditorColorButtonProps> = ({
  color,
  isSelected,
  onClick,
  isDiagonal,
  isGradient,
  isOutlined,
  icon,
  badge,
  hasSelectedCheck = true,
  "aria-label": ariaLabel,
  "data-test": dataTest,
}) => {
  const { isSmallScreen } = useMediaQuery();

  const size = (() => {
    if (isSmallScreen) {
      return isSelected ? 32 : 37;
    }

    return isSelected ? 37 : 41;
  })();

  return (
    <Box
      onClick={onClick}
      role="button"
      aria-label={ariaLabel}
      data-test={dataTest}
      sx={{
        width: size,
        height: size,
        bgcolor: !isDiagonal && !isGradient ? color : undefined,
        background: isGradient
          ? LIVE_EDITOR_RAINBOW_GRADIENT
          : isDiagonal
            ? BLACK_AND_WHITE_GRADIENT_BACKGROUND
            : undefined,
        borderRadius: 1,
        position: "relative",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "width 0.1s ease, height 0.1s ease",
        border: isDiagonal ? "1px solid black" : "none",
        ...(isOutlined && {
          border: "2px dashed",
          borderColor: (theme) => theme.palette.border.neutral.moderate,
        }),

        ...(isSelected && {
          "&::after": {
            content: '""',
            position: "absolute",
            top: -5,
            left: -5,
            right: -5,
            bottom: -5,
            borderRadius: 1,
            border: "3px solid",
            borderColor: (theme) => theme.palette.text.brand.intense,
            zIndex: 1,
          },
        }),
      }}
    >
      {isSelected && hasSelectedCheck ? (
        <DashboardIcon vibe="text-brand-intense" size="large">
          <Check />
        </DashboardIcon>
      ) : (
        icon
      )}
      {badge}
    </Box>
  );
};
